import React, { useState } from 'react';
import { TrendingDown, ShieldCheck, Info, ChevronDown, ChevronUp, Calculator } from 'lucide-react';

const KIND_STYLES = {
  tax_savings: { box: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-900', sub: 'text-emerald-700', Icon: TrendingDown },
  penalty_avoided: { box: 'bg-amber-50 border-amber-200', text: 'text-amber-900', sub: 'text-amber-700', Icon: ShieldCheck },
  risk_reduction: { box: 'bg-sky-50 border-sky-200', text: 'text-sky-900', sub: 'text-sky-700', Icon: ShieldCheck },
  default: { box: 'bg-gray-50 border-gray-200', text: 'text-gray-800', sub: 'text-gray-600', Icon: Calculator },
};

/** Per-topic illustrative savings range (computed server-side by savings_estimator). */
export const SavingsEstimate = ({ estimate, topicId }) => {
  const [open, setOpen] = useState(false);
  if (!estimate || !estimate.label) return null;

  const style = KIND_STYLES[estimate.kind] || KIND_STYLES.default;
  const { Icon } = style;
  const hasNumbers = Boolean(estimate.display);
  const hasDetails = (estimate.assumptions && estimate.assumptions.length > 0) || estimate.note;

  return (
    <div className={`rounded-lg border p-4 ${style.box}`} data-testid={`savings-estimate-${topicId}`}>
      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
        <Icon className={`hidden sm:block h-5 w-5 mt-0.5 shrink-0 ${style.sub}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-medium uppercase tracking-wide ${style.sub}`}>{estimate.label}</p>
          {hasNumbers ? (
            <p className={`text-xl font-bold ${style.text}`} data-testid={`savings-estimate-value-${topicId}`}>
              {estimate.display}
            </p>
          ) : (
            estimate.note && <p className={`text-sm ${style.text}`}>{estimate.note}</p>
          )}
          {hasNumbers && (
            <p className={`text-xs mt-1 ${style.sub}`}>Illustrative federal range — not a guarantee</p>
          )}
        </div>
        {hasDetails && hasNumbers && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className={`self-start shrink-0 text-xs font-medium flex items-center gap-1 ${style.sub} hover:underline`}
            aria-expanded={open}
            data-testid={`savings-estimate-toggle-${topicId}`}
          >
            How we estimated {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        )}
      </div>
      {open && hasNumbers && (
        <div className={`mt-3 sm:pl-8 text-xs space-y-1 ${style.sub}`} data-testid={`savings-estimate-details-${topicId}`}>
          <ul className="list-disc pl-4 space-y-1">
            {(estimate.assumptions || []).map((a) => <li key={a}>{a}</li>)}
          </ul>
          {estimate.note && <p className="italic">{estimate.note}</p>}
        </div>
      )}
    </div>
  );
};

/** Report-level combined range. */
export const SavingsSummary = ({ summary }) => {
  if (!summary || !summary.display) return null;
  return (
    <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-5" data-testid="savings-summary">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
          <TrendingDown className="h-5 w-5 text-emerald-700" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-emerald-800">Potential annual tax savings to discuss</p>
          <p className="text-2xl font-bold text-emerald-900 break-words" data-testid="savings-summary-value">{summary.display}</p>
          {summary.largest?.display && (
            <p className="text-sm text-emerald-800 mt-1" data-testid="savings-summary-largest">
              Largest single opportunity: <span className="font-semibold">{summary.largest.topic_name}</span> ({summary.largest.display})
            </p>
          )}
          <p className="text-xs text-emerald-700 mt-1">
            Across {summary.count} {summary.count === 1 ? 'topic' : 'topics'}. {summary.note}
          </p>
          <p className="text-xs text-gray-500 mt-2 flex items-start gap-1">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />
            <span>{summary.disclaimer}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SavingsEstimate;
